import { useState, useRef, useCallback } from "react";

interface IDCardUploadProps {
  frontImage: string | null;
  backImage: string | null;
  onFrontChange: (val: string | null) => void;
  onBackChange: (val: string | null) => void;
  disabled?: boolean;
}

const MAX_WIDTH = 1400;
const JPEG_QUALITY = 0.72;
const MAX_FILE_MB = 10;

function compressImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let w = img.width;
        let h = img.height;
        if (w > MAX_WIDTH) {
          h = Math.round((h * MAX_WIDTH) / w);
          w = MAX_WIDTH;
        }
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Canvas not supported"));
          return;
        }
        ctx.drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
        resolve(dataUrl);
      };
      img.onerror = () => reject(new Error("圖片載入失敗"));
      img.src = e.target?.result as string;
    };
    reader.onerror = () => reject(new Error("檔案讀取失敗"));
    reader.readAsDataURL(file);
  });
}

function UploadBox({
  label,
  sublabel,
  icon,
  image,
  onImageChange,
  disabled,
}: {
  label: string;
  sublabel: string;
  icon: string;
  image: string | null;
  onImageChange: (val: string | null) => void;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState("");

  const processFile = useCallback(
    async (file: File) => {
      setError("");
      if (!file.type.startsWith("image/")) {
        setError("僅支援圖片檔案（JPG / PNG）");
        return;
      }
      if (file.size > MAX_FILE_MB * 1024 * 1024) {
        setError(`檔案過大，請選擇 ${MAX_FILE_MB}MB 以下的圖片`);
        return;
      }
      try {
        const compressed = await compressImage(file);
        onImageChange(compressed);
      } catch {
        setError("圖片處理失敗，請重新上傳");
      }
    },
    [onImageChange]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  return (
    <div className="flex flex-col gap-1.5">
      <p className="font-body text-xs font-medium text-stone-500">
        {label}
        <span className="text-stone-300 font-normal ml-1">{sublabel}</span>
      </p>

      <div
        onClick={() => !disabled && !image && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled && !image) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={[
          "relative rounded-xl border-2 border-dashed overflow-hidden cursor-pointer transition-all",
          "flex items-center justify-center",
          "h-36 md:h-40",
          image ? "border-stone-200" : dragOver ? "border-rose-400 bg-rose-50/40" : "border-stone-300 bg-stone-50 hover:border-stone-400 hover:bg-stone-100/60",
          disabled && !image ? "opacity-50 cursor-not-allowed" : "",
        ].join(" ")}
      >
        {image ? (
          <>
            <img
              src={image}
              alt={label}
              className="w-full h-full object-contain"
            />
            {!disabled && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center gap-2 opacity-0 hover:opacity-100 transition-opacity">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    inputRef.current?.click();
                  }}
                  className="font-body text-xs font-medium px-3 py-1.5 rounded-full bg-white text-stone-800 hover:bg-stone-100 transition-colors whitespace-nowrap"
                >
                  <i className="ri-refresh-line mr-1" />
                  更換
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onImageChange(null);
                  }}
                  className="font-body text-xs font-medium px-3 py-1.5 rounded-full bg-red-50 text-red-700 hover:bg-red-100 transition-colors whitespace-nowrap"
                >
                  <i className="ri-delete-bin-line mr-1" />
                  刪除
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center gap-2 text-center px-4">
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-stone-200/70">
              <i className={`${icon} text-stone-500 text-lg`} />
            </div>
            <p className="font-body text-[11px] text-stone-400 leading-relaxed">
              點擊或拖曳上傳
              <br />
              <span className="text-stone-300">JPG、PNG，最大 {MAX_FILE_MB}MB</span>
            </p>
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/jpg,image/webp"
          onChange={handleInputChange}
          className="hidden"
          disabled={disabled}
        />
      </div>

      {error && (
        <p className="font-body text-[11px] text-red-500 flex items-center gap-1">
          <i className="ri-error-warning-line" />
          {error}
        </p>
      )}
    </div>
  );
}

export default function IDCardUpload({
  frontImage,
  backImage,
  onFrontChange,
  onBackChange,
  disabled,
}: IDCardUploadProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <UploadBox
        label="身分證正面"
        sublabel="（人像面）"
        icon="ri-id-card-line"
        image={frontImage}
        onImageChange={onFrontChange}
        disabled={disabled}
      />
      <UploadBox
        label="身分證反面"
        sublabel="（國徽面）"
        icon="ri-landscape-line"
        image={backImage}
        onImageChange={onBackChange}
        disabled={disabled}
      />
    </div>
  );
}